import React, { useMemo, useState, useEffect } from "react";
import "./PromptDictionary.css";

export type PromptItem = {
  prompt: string;
  label?: React.ReactNode;
};

export type PromptSection = {
  id: string;
  title: string;
  description: string;
  items: PromptItem[];
};

export const promptSections: PromptSection[] = [
  {
    id: "tableau",
    title: "A. Tableau Dashboard Insights",
    description:
      "The following prompts retrieve data from core dashboard metrics and Tableau dashboards.",
    items: [
      { prompt: "Using core metrics, give me total projects won in 2025" },
      {
        prompt:
          "Give project count and jobs created for the automotive sector in Franklin County using core metrics in 2024",
      },
      {
        prompt:
          "What is the total program committed and program value as of March 1st for the Western Region by sector",
      },
      {
        prompt:
          "Show me project count, jobs created, jobs created payroll, and capital investment for the advanced manufacturing sector in 2023 using core metrics",
      },
      {
        prompt:
          "Can you give me details for Project Spark – Columbus from Tableau",
      },
    ],
  },
  {
    id: "box-content",
    title: "B. JO Internal Intelligence (Box Content)",
    description:
      "The following prompts retrieve insights from stored research documents and general knowledge repositories.",
    items: [
      { prompt: "What is the travel hotel daily limit per Travel policy?" },
      { prompt: "What is the flight allowance for travel to Las Vegas?" },
      {
        prompt:
          "What are the key economic and business intelligence metrics that highlight Ohio's competitive positioning across sectors?",
      },
      {
        prompt:
          "How does Ohio rank nationally in manufacturing jobs, automotive manufacturing, aerospace/defense employment, and warehousing and storage services?",
      },
      { prompt: "What defines a mega project?" },
      {
        prompt:
          "How does Ohio rank for natural disasters compared to other states?",
      },
    ],
  },
  {
    id: "jobit",
    title: "C. JOBIT Insights",
    description:
      "The following prompts pull economic metrics, rankings, and benchmarking insights from JOBIT.",
    items: [
      {
        prompt:
          "Acting as a Director, pull the percent of gross state product that JobsOhio's 10 sectors account for from JOBIT",
      },
      {
        prompt: "What is Ohio's ranking for engines according to JOBIT?",
      },
      {
        prompt:
          "What are Ohio's rankings in terms of corporate tax structure, energy availability, workforce training programs, and business incentive programs for 2025?",
      },
      {
        prompt:
          "Acting as a research analyst, pull data exclusively from JOBIT about Ohio's ranking for engine manufacturing",
      },
    ],
  },
  {
    id: "website",
    title: "D. JobsOhio Website Information",
    description:
      "The following prompts pull publicly available information from the JobsOhio website.",
    items: [
      { prompt: "Can you list the largest employers in Ohio?" },
      {
        prompt:
          "Can you provide details about JobsOhio's international investments?",
      },
      {
        prompt: "What are the Energy Opportunity Initiatives by JobsOhio?",
      },
    ],
  },
  {
    id: "general",
    title: "E. General Purpose",
    description:
      "The following prompts address internal strategy and conceptual frameworks.",
    items: [
      {
        prompt:
          "What is the 4A AI strategy and the 5 AI pillars of execution?",
      },
      { prompt: "Draft executive level email on travel policies" },
      {
        prompt:
          "Just wanted to follow up from our last meeting, I think we maybe missed a few points there.\nI was looking at the numbers again and it dont really add up the same way as before.\nAlso I think John mentioned something about the deadline being moved? not 100% sure tho.\nIf thats the case we might need to recheck everything again just to be safe.\nSorry if I misunderstood anything, was a bit rushed that day.\nCan you confirm what the actual plan is now when you get a chance.\nAlso should we loop in Jane or she already aware?\nLet me know, thanks alot.\n\nWrite the above email in a professional tone and make it short to the point",
      },
    ],
  },
  {
    id: "salesforce",
    title: "F. Salesforce Project Data (JOSF)",
    description:
      "The following prompts retrieve project lifecycle, status, and metadata from Salesforce (JOSF).",
    items: [
      {
        prompt:
          'Give me projects that have "tariff" mentioned in the project summary for projects created in 2025 using JOSF',
      },
      {
        prompt:
          "Show me the project description and important dates for these projects",
      },
    ],
  },
  {
    id: "deep-search",
    title: "G. Deep Search Feature",
    description:
      "Deep Search performs multi-source analysis to generate detailed, report-style insights (longer response time).",
    items: [
      {
        prompt:
          "Perform deep research on project progress (won, executed, and in-pipeline) across the advanced manufacturing sector. Compare data across 2024, 2025, and the current year. Analyze key effects, outcomes, and areas for improvement.",
      },
    ],
  },
  {
    id: "focus-mode",
    title: "H. Focus Mode",
    description:
      "This feature enables you to select a specific source like Box, JOBIT, Salesforce, Tableau or the JobsOhio website to get more accurate and relevant results.",
    items: [
      {
        prompt: "What key trends can you identify from this file in terms of industry dominance, growth patterns, and the presence of companies based in Ohio?",
        label: <>What key trends can you identify from this <strong>file</strong> in terms of industry dominance, growth patterns, and the presence of companies based in Ohio?</>,
      },
      {
        prompt: "Give me a summary of information in this folder",
        label: <>Give me a summary of information in this <strong>folder</strong></>,
      },
      {
        prompt: "Based on this dashboard, give me the total projects won in 2025",
        label: <>Based on this <strong>dashboard</strong>, give me the total projects won in 2025</>,
      },
      {
        prompt: "Based on this project give me the important dates",
        label: <>Based on this <strong>project</strong> give me the important dates</>,
      },
    ],
  },
];

/* helpers */
const countPrompts = (sections: PromptSection[]) =>
  sections.reduce((n, s) => n + s.items.length, 0);

const filterPromptSections = (sections: PromptSection[], q: string) => {
  if (!q.trim()) return sections;
  const lower = q.toLowerCase();
  return sections
    .map((s) => ({
      ...s,
      items: s.items.filter((i) => i.prompt.toLowerCase().includes(lower)),
    }))
    .filter((s) => s.items.length > 0);
};

const PromptDictionary: React.FC = () => {
  const [query, setQuery] = useState("");
  const [activeSectionId, setActiveSectionId] = useState<string>("tableau");
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const sections = useMemo(() => promptSections, []);
  const filteredSections = useMemo(
    () => filterPromptSections(sections, query),
    [sections, query]
  );
  const totalPrompts = useMemo(() => countPrompts(sections), [sections]);

  useEffect(() => {
    const first = filteredSections[0]?.id || "tableau";
    setActiveSectionId(first);
  }, [filteredSections]);

  const scrollToSection = (id: string) => {
    setActiveSectionId(id);
    const el = document.getElementById(`pd-sec-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  return (
    <div className="hc-layout" role="tabpanel" aria-label="Prompt Library">
      {/* Left navigation */}
      <aside className="hc-nav" aria-label="Prompt Library navigation">
        <div className="hc-nav-card">
          <div className="hc-search">
            <i className="fas fa-magnifying-glass" aria-hidden="true" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search prompts…"
              aria-label="Search prompts"
            />
            {query.trim() && (
              <button
                type="button"
                className="hc-clear"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                title="Clear"
              >
                <i className="fas fa-times" />
              </button>
            )}
          </div>

          <div className="hc-nav-title">Categories</div>
          <div className="hc-nav-list">
            {filteredSections.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`hc-nav-item ${activeSectionId === s.id ? "active" : ""}`}
                onClick={() => scrollToSection(s.id)}
              >
                <span className="hc-nav-item-text">{s.title}</span>
                <span className="hc-nav-item-count">{s.items.length}</span>
              </button>
            ))}
          </div>

          <div className="hc-nav-tip">
            <i className="fas fa-shield-halved pd-tip-icon" aria-hidden="true" />{" "}
            JO-GPT follows strict access controls and will generate responses only from data sources and content that the user is authorized to access.
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="hc-main">
        <div className="pd-intro-card">
          <div className="pd-intro-title">Prompt Library</div>
          <div className="pd-intro-sub">
            Sample prompts to help you explore and effectively utilize JO-GPT.
            Prompts are grouped by the type of data they retrieve.
          </div>
          <div className="pd-intro-stat">
            <span className="hc-pill">{totalPrompts}</span> prompts across{" "}
            <span className="hc-pill">{sections.length}</span> categories
          </div>
        </div>

        {filteredSections.length === 0 ? (
          <div className="hc-empty">
            <div className="hc-empty-title">No prompts found</div>
            <div className="hc-empty-sub">Try a different keyword.</div>
          </div>
        ) : (
          filteredSections.map((section) => (
            <section
              key={section.id}
              id={`pd-sec-${section.id}`}
              className="hc-section"
              aria-label={section.title}
            >
              <div className="hc-section-head">
                <div className="hc-section-title">{section.title}</div>
                <div className="hc-section-meta">
                  {section.items.length}{" "}
                  {section.items.length === 1 ? "prompt" : "prompts"}
                </div>
              </div>

              <div className="pd-section-desc">{section.description}</div>

              <div className="pd-prompt-list">
                {section.items.map((item, idx) => {
                  const key = `${section.id}:${idx}`;
                  const isCopied = copiedKey === key;

                  return (
                    <div key={key} className="pd-prompt-card">
                      <div className="pd-prompt-text">{item.label ?? item.prompt}</div>
                      <div className="pd-prompt-actions">
                        <button
                          type="button"
                          className="pd-copy-btn"
                          onClick={() => handleCopy(item.prompt, key)}
                        >
                          <i
                            className={`fas ${isCopied ? "fa-check" : "fa-copy"}`}
                            aria-hidden="true"
                          />
                          <span>{isCopied ? "Copied!" : "Copy Prompt"}</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))
        )}
      </main>
    </div>
  );
};

export default PromptDictionary;
